import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  ICustomIdInputFieldSettingsModel,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
  getPopulatesPropsSettingsConfigFormly,
  populatesPropsParseIntoSettingsFormModel,
  populatesPropsUpdateFromSettingsFormModel,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  parseRequiredValidatorParseFieldIntoModel,
  parseInvalidInputValidatorParseFieldIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  setRequiredValidatorUpdateFieldFromModel,
  setInvalidInputFormDataIntoField,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import {
  getRequiredValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import { IFormFieldsValidationModel } from 'src/app/core/models/form-fields-formly-validation.model';
import {
  CRVS_DOCUMENT_TYPE_DEFINITIONS,
  CRVSDocumentTypes,
  ICRVSDocumentTypeDefinition,
} from '../../../models/crvs-document-type.model';

interface IDoubleIdInputFieldContentConfig {
  url: string;
  useBaseUrl: boolean;
  endpointCode: string;
  allowedDocumentTypes: Record<CRVSDocumentTypes, boolean>;
  firstIdentityLabel: string;
  firstIdentityPlaceholder: string;
  firstIdentityValueKey: string;
  firstIdentityDocumentTypeKey: string;
  firstIdentityCountryValueKey: string;
  secondIdentityLabel: string;
  secondIdentityPlaceholder: string;
  secondIdentityValueKey: string;
  secondIdentityDocumentTypeKey: string;
  secondIdentityCountryValueKey: string;
  mixpanelEventAnalytics?: boolean;
}

interface IDoubleIdInputConfigurationFormModel
  extends ICustomIdInputFieldSettingsModel {
  doubleIdInputFieldContentConfig?: IDoubleIdInputFieldContentConfig;
}

export const doubleIdInputGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: {
        ...fieldsBaseConfigurationFormModel(),
        doubleIdInputFieldContentConfig: {
          url: '/integration/v1/fetch/sync',
          useBaseUrl: true,
          endpointCode: '',
          allowedDocumentTypes: [],
          firstIdentityLabel: '',
          firstIdentityPlaceholder: '',
          firstIdentityValueKey: '',
          firstIdentityDocumentTypeKey: '',
          secondIdentityLabel: '',
          secondIdentityPlaceholder: '',
          secondIdentityValueKey: '',
          secondIdentityDocumentTypeKey: '',
        },
      },
      formlyForm: [
        ...getDoubleIdInputFieldSettingsConfig(),
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        ...getPopulatesPropsSettingsConfigFormly(
          formBuilderServiceInstance,
          []
        ),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const doubleIdInputFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IDoubleIdInputConfigurationFormModel => {
  const model: IDoubleIdInputConfigurationFormModel =
    populatesPropsParseIntoSettingsFormModel(field);

  const existingTypes = field.props?.['allowedDocumentTypes'] || [];
  const allowedDocumentTypes = Object.values(CRVSDocumentTypes).reduce(
    (acc, type) => ({
      ...acc,
      [type]: existingTypes.some(
        (doc: ICRVSDocumentTypeDefinition) => doc.value === type
      ),
    }),
    {} as Record<CRVSDocumentTypes, boolean>
  );

  model.doubleIdInputFieldContentConfig = {
    url: field.props?.['url'],
    useBaseUrl: field.props?.['useBaseUrl'],
    endpointCode: field.props?.['endpointCode'],
    allowedDocumentTypes: allowedDocumentTypes,
    firstIdentityLabel: field.props?.['firstIdentityLabel'],
    firstIdentityPlaceholder: field.props?.['firstIdentityPlaceholder'],
    firstIdentityValueKey: field.props?.['firstIdentityValueKey'],
    firstIdentityCountryValueKey: field.props?.['firstIdentityCountryValueKey'],
    firstIdentityDocumentTypeKey: field.props?.['firstIdentityDocumentTypeKey'],
    secondIdentityLabel: field.props?.['secondIdentityLabel'],
    secondIdentityPlaceholder: field.props?.['secondIdentityPlaceholder'],
    secondIdentityValueKey: field.props?.['secondIdentityValueKey'],
    secondIdentityDocumentTypeKey:
      field.props?.['secondIdentityDocumentTypeKey'],
    secondIdentityCountryValueKey:
      field.props?.['secondIdentityCountryValueKey'],
    mixpanelEventAnalytics: field.props?.['mixpanelEventAnalytics'],
  };

  return model;
};

export const doubleIdInputFieldUpdateFromSettingsFormModel = (
  model: IDoubleIdInputConfigurationFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field = baseFieldUpdateFromSettingsFormModel(model, field);
  field = populatesPropsUpdateFromSettingsFormModel(model, field);

  const documentTypeConfigs = Object.entries(
    model.doubleIdInputFieldContentConfig?.allowedDocumentTypes || {}
  )
    .filter(entry => typeof entry[1] === 'boolean' && entry[1] === true)
    .map(([type]) => {
      const definition =
        CRVS_DOCUMENT_TYPE_DEFINITIONS[type as CRVSDocumentTypes];
      return definition;
    });

  if (field.props) {
    field.props['url'] = model.doubleIdInputFieldContentConfig?.url;
    field.props['useBaseUrl'] =
      model.doubleIdInputFieldContentConfig?.useBaseUrl;
    field.props['endpointCode'] =
      model.doubleIdInputFieldContentConfig?.endpointCode;
    field.props['allowedDocumentTypes'] = documentTypeConfigs;
    field.props['firstIdentityLabel'] =
      model.doubleIdInputFieldContentConfig?.firstIdentityLabel;
    field.props['firstIdentityPlaceholder'] =
      model.doubleIdInputFieldContentConfig?.firstIdentityPlaceholder;
    field.props['firstIdentityValueKey'] =
      model.doubleIdInputFieldContentConfig?.firstIdentityValueKey;
    field.props['firstIdentityDocumentTypeKey'] =
      model.doubleIdInputFieldContentConfig?.firstIdentityDocumentTypeKey;
    field.props['firstIdentityCountryValueKey'] =
      model.doubleIdInputFieldContentConfig?.firstIdentityCountryValueKey;
    field.props['secondIdentityLabel'] =
      model.doubleIdInputFieldContentConfig?.secondIdentityLabel;
    field.props['secondIdentityPlaceholder'] =
      model.doubleIdInputFieldContentConfig?.secondIdentityPlaceholder;
    field.props['secondIdentityValueKey'] =
      model.doubleIdInputFieldContentConfig?.secondIdentityValueKey;
    field.props['secondIdentityDocumentTypeKey'] =
      model.doubleIdInputFieldContentConfig?.secondIdentityDocumentTypeKey;
    field.props['secondIdentityCountryValueKey'] =
      model.doubleIdInputFieldContentConfig?.secondIdentityCountryValueKey;
    field.props['mixpanelEventAnalytics'] =
      model.doubleIdInputFieldContentConfig?.mixpanelEventAnalytics;
  } else {
    field.props = {
      url: model.doubleIdInputFieldContentConfig?.url,
      useBaseUrl: model.doubleIdInputFieldContentConfig?.useBaseUrl,
      endpointCode: model.doubleIdInputFieldContentConfig?.endpointCode,
      allowedDocumentTypes:
        model.doubleIdInputFieldContentConfig?.allowedDocumentTypes,
      firstIdentityLabel:
        model.doubleIdInputFieldContentConfig?.firstIdentityLabel,
      firstIdentityPlaceholder:
        model.doubleIdInputFieldContentConfig?.firstIdentityPlaceholder,
      firstIdentityValueKey:
        model.doubleIdInputFieldContentConfig?.firstIdentityValueKey,
      firstIdentityDocumentTypeKey:
        model.doubleIdInputFieldContentConfig?.firstIdentityDocumentTypeKey,
      firstIdentityCountryValueKey:
        model.doubleIdInputFieldContentConfig?.firstIdentityCountryValueKey,
      secondIdentityLabel:
        model.doubleIdInputFieldContentConfig?.secondIdentityLabel,
      secondIdentityPlaceholder:
        model.doubleIdInputFieldContentConfig?.secondIdentityPlaceholder,
      secondIdentityValueKey:
        model.doubleIdInputFieldContentConfig?.secondIdentityValueKey,
      secondIdentityDocumentTypeKey:
        model.doubleIdInputFieldContentConfig?.secondIdentityDocumentTypeKey,
      secondIdentityCountryValueKey:
        model.doubleIdInputFieldContentConfig?.secondIdentityCountryValueKey,
      mixpanelEventAnalytics:
        model.doubleIdInputFieldContentConfig?.mixpanelEventAnalytics,
    };
  }

  return field;
};

export const doubleIdInputFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  model = parseInvalidInputValidatorParseFieldIntoModel(model, field);

  return model;
};

export const doubleIdInputFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field = setRequiredValidatorUpdateFieldFromModel(model, field);
  field = setInvalidInputFormDataIntoField(model, field);

  return field;
};

export const doubleIdInputFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const doubleIdInputFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

export const getDoubleIdInputFieldSettingsConfig = (): FormlyFieldConfig[] => {
  return [
    {
      key: 'doubleIdInputFieldContentConfig',
      id: 'doubleIdInputFieldContentConfig',
      type: EIremboFormlyFieldTypes['formly-group'],
      props: {
        dragDisabled: false,
      },
      fieldGroup: [
        {
          key: 'url',
          type: 'input',
          props: {
            label: 'API URL',
            required: true,
            placeholder: 'Enter API URL',
          },
        },
        {
          key: 'useBaseUrl',
          type: 'checkbox',
          props: {
            label: 'Use Base URL',
          },
        },
        {
          key: 'mixpanelEventAnalytics',
          type: 'checkbox',
          defaultValue: false,
          props: {
            label: 'Enable MixPanel Analytics',
            formCheck: 'switch',
          },
        },
        {
          key: 'endpointCode',
          type: 'input',
          props: {
            label: 'Endpoint Code',
            required: true,
            placeholder: 'Enter Endpoint Code',
          },
        },
        {
          key: 'allowedDocumentTypes',
          type: EIremboFormlyFieldTypes.multicheckbox,
          props: {
            label: 'Allowed Document Types',
            required: true,
            options: Object.values(CRVSDocumentTypes).map(value => ({
              label: CRVS_DOCUMENT_TYPE_DEFINITIONS[value].label,
              value: CRVS_DOCUMENT_TYPE_DEFINITIONS[value].value,
            })),
          },
        },
        {
          key: 'firstIdentityLabel',
          type: 'input',
          props: {
            label: 'First Identity Label',
            required: true,
            placeholder: 'Enter First Identity Label',
          },
        },
        {
          key: 'firstIdentityPlaceholder',
          type: 'input',
          props: {
            label: 'First Identity Placeholder',
            required: true,
            placeholder: 'Enter First Identity Placeholder',
          },
        },
        {
          key: 'firstIdentityValueKey',
          type: 'input',
          props: {
            label: 'First Identity Value Key',
            required: true,
            placeholder: 'Enter First Identity Value Key',
          },
        },
        {
          key: 'firstIdentityDocumentTypeKey',
          type: 'input',
          props: {
            label: 'First Identity Document Type Key',
            required: true,
            placeholder: 'Enter First Identity Document Type Key',
          },
        },
        {
          key: 'firstIdentityCountryValueKey',
          type: 'input',
          props: {
            label: 'First Identity Country ValueKey',
            required: true,
            placeholder: 'Enter First Identity Document Type Key',
          },
        },
        {
          key: 'secondIdentityLabel',
          type: 'input',
          props: {
            label: 'Second Identity Label',
            required: true,
            placeholder: 'Enter Second Identity Label',
          },
        },
        {
          key: 'secondIdentityPlaceholder',
          type: 'input',
          props: {
            label: 'Second Identity Placeholder',
            required: true,
            placeholder: 'Enter Second Identity Placeholder',
          },
        },
        {
          key: 'secondIdentityValueKey',
          type: 'input',
          props: {
            label: 'Second Identity Value Key',
            required: true,
            placeholder: 'Enter Second Identity Value Key',
          },
        },
        {
          key: 'secondIdentityDocumentTypeKey',
          type: 'input',
          props: {
            label: 'Second Identity Document Type Key',
            required: true,
            placeholder: 'Enter Second Identity Document Type Key',
          },
        },
        {
          key: 'secondIdentityCountryValueKey',
          type: 'input',
          props: {
            label: 'Second Identity Country ValueKey',
            required: true,
            placeholder: 'Enter Second Identity Document Type Key',
          },
        },
      ],
    },
  ];
};
